import type { BillingCategory, BillingSettlement, CategoryOrGroup } from './api';

// The Billing page's arithmetic: which homes have it, and how a settled month
// reads — its total split into use and fixed costs, its bills next to the
// month before, the home's use per m² against the association's. Copied from
// the mobile lib/billing.ts — keep in sync. Only type imports, so `node
// --test` runs it (tests/billing.test.ts).

// The homes the Billing page shows: groups whose project has it on
// (project_billing) and that the user owns — bills are the owner's business,
// and portal-api answers nobody else. 'owner' is what an admin was called
// until 2026-09-23 (see isAdmin in lib/api.ts).
export function billingHomes<T extends Pick<CategoryOrGroup, 'type' | 'project_billing' | 'role'>>(
  entries: T[],
): T[] {
  return entries.filter(
    (e) => e.type === 'g' && e.project_billing === true && (e.role === 'admin' || e.role === 'owner'),
  );
}

export function hasBilling(entries: Pick<CategoryOrGroup, 'type' | 'project_billing' | 'role'>[]): boolean {
  return billingHomes(entries).length > 0;
}

// What a month came to: `fixed` is the fixed charges (fee lines), `corrections`
// the changes to earlier months it carried, `usage` the rest — what the home
// used and its share of what was used in common. Null amounts count as 0;
// the whole is null when the association settles use only.
export function monthTotals(
  s: BillingSettlement,
): { total: number; usage: number; fixed: number; corrections: number } | null {
  if (!s.money) return null;
  let fixed = 0;
  let corrections = 0;
  let all = 0;
  for (const c of s.categories) {
    for (const l of c.lines) {
      const a = l.amount ?? 0;
      all += a;
      if (l.kind === 'correction') corrections += a;
      else if (l.fee) fixed += a;
    }
  }
  return { total: s.total ?? all, usage: all - fixed - corrections, fixed, corrections };
}

// A bill's own measured use: its direct lines that print a quantity, summed
// per unit (the first unit when a bill mixes them, which it should not).
export function categoryUsage(c: BillingCategory): { quantity: number; unit: string } | null {
  let unit: string | undefined;
  let quantity = 0;
  for (const l of c.lines) {
    if (l.kind !== 'direct' || l.quantity_kind !== 'usage' || l.quantity == null || !l.unit) continue;
    if (unit == null) unit = l.unit;
    if (l.unit === unit) quantity += l.quantity;
  }
  return unit == null ? null : { quantity, unit };
}

export type CategoryRow = {
  code: string;
  name: string;
  // fee: a bill made only of fixed charges, shown as one.
  kind: BillingCategory['kind'];
  amount: number | null;
  usage: { quantity: number; unit: string } | null;
  // Share of the month's total, 0..1; null without money.
  share: number | null;
  // Change against the same bill the month before (cur/prev − 1); null when
  // either month has no amount for it.
  change: number | null;
};

// The month's bills, largest first, each next to the same bill of `prev` (the
// settled month before, if any).
export function categoryRows(s: BillingSettlement, prev?: BillingSettlement): CategoryRow[] {
  const totals = monthTotals(s);
  const prevAmount = new Map<string, number>();
  for (const c of prev?.categories ?? []) if (c.amount != null) prevAmount.set(c.code, c.amount);
  const rows = s.categories.map((c): CategoryRow => {
    const before = prevAmount.get(c.code);
    const allFee = c.lines.length > 0 && c.lines.every((l) => l.fee);
    return {
      code: c.code,
      name: c.name,
      kind: allFee ? 'fee' : c.kind,
      amount: c.amount ?? null,
      usage: categoryUsage(c),
      share: totals && totals.total > 0 && c.amount != null ? c.amount / totals.total : null,
      change: c.amount != null && before != null && before !== 0 ? c.amount / before - 1 : null,
    };
  });
  return rows.sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0));
}

// Measured lines: split by the home's own meters, directly or by a formula over
// them — what an average per m² compares like with like.
const MEASURED = new Set(['by_metered_usage', 'by_formula']);

// The home's measured use of one bill per m² against the association's
// average that month. Only for a month the user had whole (part of a month
// would read as low use) and a home with a known floor area.
export function usePerM2(
  s: BillingSettlement,
  code: string,
  areaM2: number | undefined,
): { own: number; average: number; unit: string; ratio: number } | null {
  if (!areaM2 || areaM2 <= 0 || s.from !== s.period_from || s.to !== s.period_to) return null;
  const c = s.categories.find((x) => x.code === code);
  if (!c) return null;
  const own = new Map<string, number>();
  for (const l of c.lines) {
    if (l.kind !== 'direct' || !MEASURED.has(l.method) || l.quantity_kind !== 'usage') continue;
    if (l.quantity == null || !l.unit) continue;
    own.set(l.unit, (own.get(l.unit) ?? 0) + l.quantity);
  }
  for (const a of s.averages) {
    if (a.category_code !== code || !own.has(a.unit) || a.per_m2 <= 0) continue;
    const mine = (own.get(a.unit) as number) / areaM2;
    return { own: mine, average: a.per_m2, unit: a.unit, ratio: mine / a.per_m2 - 1 };
  }
  return null;
}

// The settled month before `label` in a newest-first list, if it is there.
export function previousSettlement(list: BillingSettlement[], label: string): BillingSettlement | undefined {
  const i = list.findIndex((s) => s.label === label);
  return i >= 0 ? list[i + 1] : undefined;
}

// Money with a space between thousands and two decimals, then the currency
// code: "1 234.50 NOK". The typographic minus, so a credit reads at a glance.
export function formatMoney(amount: number | null | undefined, currency: string): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  const cents = Math.round(Math.abs(amount) * 100);
  const whole = Math.floor(cents / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const sign = amount < 0 && cents > 0 ? '−' : '';
  return `${sign}${whole}.${String(cents % 100).padStart(2, '0')} ${currency}`;
}

// A price per unit, as precise as a tariff is: "42.80 NOK/m³", "0.9812 NOK/kWh".
export function formatRate(rate: number | null | undefined, currency: string, unit?: string): string {
  if (rate == null || !Number.isFinite(rate)) return '—';
  const decimals = Math.abs(rate) < 10 && Math.round(rate * 100) !== rate * 100 ? 4 : 2;
  return `${rate.toFixed(decimals)} ${currency}${unit ? `/${unit}` : ''}`;
}

// A quantity in its unit: up to two decimals, thousands spaced — "11.4 m³",
// "1 240 kWh".
export function formatQuantity(q: number | null | undefined, unit?: string): string {
  if (q == null || !Number.isFinite(q)) return '—';
  const rounded = Math.abs(q) >= 100 ? Math.round(q) : parseFloat(q.toFixed(2));
  const [int, dec] = String(Math.abs(rounded)).split('.');
  const body = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + (dec ? `.${dec}` : '');
  return `${rounded < 0 ? '−' : ''}${body}${unit ? ` ${unit}` : ''}`;
}

// The month a settlement label names ("2026-05"), or null for a quarter, a
// year or a pre-settlement's own label.
export function labelMonth(label: string): { year: number; month: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(label);
  return m ? { year: Number(m[1]), month: Number(m[2]) } : null;
}

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// The name of a settled month in the UI language — "August 2026", "August
// 2026" in Norwegian too, capitalized — or the label itself for a quarter, a
// year or a pre-settlement. Month names come from Intl, as on every chart.
export function monthTitle(label: string, lang: string): string {
  const m = labelMonth(label);
  if (!m) return label;
  const name = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(m.year, m.month - 1, 15)),
  );
  return capitalize(name);
}

// A month chip's two lines: the short month name and its year.
export function monthChip(label: string, lang: string): { month: string; year: string } {
  const m = labelMonth(label);
  if (!m) return { month: label, year: '' };
  const short = new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(m.year, m.month - 1, 15)),
  );
  return { month: capitalize(short.replace(/\.$/, '')), year: String(m.year) };
}

// Days as the EU writes them, day before month: "1–31 Aug", "16 Aug – 15 Sep",
// with the year once the two ends fall in different years.
export function dayRange(from: string, to: string, lang: string): string {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const short = (y: number, m: number) =>
    new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' })
      .format(new Date(Date.UTC(y, m - 1, 15)))
      .replace(/\.$/, '');
  if (fy !== ty) return `${fd} ${short(fy, fm)} ${fy} – ${td} ${short(ty, tm)} ${ty}`;
  if (fm !== tm) return `${fd} ${short(fy, fm)} – ${td} ${short(ty, tm)}`;
  return `${fd}–${td} ${short(fy, fm)}`;
}

// A day as DD.MM.YYYY, from YYYY-MM-DD — never through Date, so no timezone
// can move it.
export function formatDay(iso: string | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}
